import { useEffect, useState, useCallback } from 'react';
import { getQuestActivities } from 'src/redux/quests/service';
import { ReactComponent as GeneralActivityIcon } from 'src/assets/icons/general_activity.svg';
import { ReactComponent as DescriptionChangeIcon } from 'src/assets/icons/description_change.svg';
import { QuestActivityType, QuestStatus } from 'src/enums/quests';
import { QuestActivity } from 'src/types/quests';
import useAppDispatch from 'src/hooks/useAppDispatch';

import {
    ActivityColumn,
    ActivityRow,
    DateContainer,
    DatePart,
    HistoryContainer,
    StyledTitle,
    TimePart,
    StyledContainer,
    IconColumn,
    StyledStar,
    VerticalLine,
    IconBox,
    StyledAttachment
} from './styled';
import { Rating } from 'src/types/rating';
import RowUpdate from 'src/shared/ActivityUpdate';
import { ReviewStatus, WorkType as WorkTypeEnum } from 'src/enums/work';
import { IAttachment } from 'src/types/attachments';

type Props = {
    questId: string;
};

const handleIcon = (activity: {
    activityType: QuestActivityType;
    hasAttachments?: boolean;
}) => {
    if (activity.hasAttachments) {
        return <StyledAttachment />;
    }
    switch (activity.activityType) {
        case QuestActivityType.DESCRIPTION_UPDATED:
            return <DescriptionChangeIcon />;
        case QuestActivityType.RATING_SUBMITTED:
        case QuestActivityType.WORK_SOLUTION_RATING:
            return <StyledStar />;
        default:
            return <GeneralActivityIcon />;
    }
};

const useExpandedDescriptions = () => {
    const [expandedDescriptions, setExpandedDescriptions] = useState<{
        [key: number]: boolean;
    }>({});

    const handleDescriptionToggle = useCallback((activityId: number) => {
        setExpandedDescriptions((prev) => ({
            ...prev,
            [activityId]: !prev[activityId]
        }));
    }, []);

    return { expandedDescriptions, handleDescriptionToggle };
};

const History: React.FC<Props> = ({ questId }) => {
    const { expandedDescriptions, handleDescriptionToggle } =
        useExpandedDescriptions();
    const dispatch = useAppDispatch();
    const [activities, setActivities] = useState<QuestActivity[]>([]);
    useEffect(() => {
        if (questId) {
            dispatch(getQuestActivities({ questId }))
                .unwrap()
                .then((fetchedActivities: QuestActivity[]) => {
                    setActivities(fetchedActivities);
                })
                .catch((error) => {
                    console.error('Failed to fetch quest activities:', error);
                });
        }
    }, [dispatch, questId]);

    return (
        <StyledContainer>
            <StyledTitle>History</StyledTitle>
            <HistoryContainer>
                {activities
                    .sort(
                        (a, b) =>
                            b.created.localeCompare(a.created) || b.id - a.id
                    )
                    .map((activity, index) => {
                        const { created, user, activityType, details } =
                            activity;
                        const {
                            text,
                            status,
                            statusChange = {},
                            ratings,
                            workType,
                            taskTitle,
                            taskId: detailsTaskId,
                            tasksToCreate,
                            tasksDependencies,
                            netWorkTime,
                            grossWorkTime,
                            rejectionReason,
                            solveByReviewer,
                            seniorReviewerName,
                            blockerDescription,
                            quickQuest,
                            attachments,
                            reviewStatus,
                            priority = {}
                        } = details as {
                            text?: string;
                            status?: QuestStatus;
                            statusChange?: {
                                from?: QuestStatus;
                                to?: QuestStatus;
                            };
                            ratings?: Rating[];
                            workType?: WorkTypeEnum;
                            taskTitle?: string;
                            taskId?: string;
                            tasksToCreate?: string[];
                            tasksDependencies?: {
                                [taskTitle: string]: (string | number)[];
                            };
                            netWorkTime?: string;
                            grossWorkTime?: string;
                            rejectionReason?: string;
                            solveByReviewer?: boolean;
                            seniorReviewerName?: string;
                            blockerDescription?: string;
                            quickQuest?: boolean;
                            attachments?: IAttachment[];
                            reviewStatus?: ReviewStatus;
                            priority?: {
                                from?: number;
                                to?: number;
                            };
                        };

                        const { from, to } = statusChange || {};

                        const date = new Date(created);
                        const datePart = date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        });
                        const timePart = date.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                        });

                        return (
                            <ActivityRow key={activity.id}>
                                <ActivityColumn>
                                    <DateContainer>
                                        <DatePart>{datePart}</DatePart>
                                        <TimePart>{timePart}</TimePart>
                                    </DateContainer>
                                </ActivityColumn>
                                <IconColumn>
                                    <IconBox>
                                        {handleIcon({
                                            activityType,
                                            hasAttachments:
                                                attachments &&
                                                attachments.length > 0
                                        })}
                                        {index !== activities.length - 1 && (
                                            <VerticalLine />
                                        )}
                                    </IconBox>
                                </IconColumn>
                                <ActivityColumn>
                                    <RowUpdate
                                        activity_type={activityType}
                                        user={user}
                                        activityId={activity.id}
                                        text={text}
                                        quickQuest={quickQuest}
                                        status={status}
                                        from={from}
                                        to={to}
                                        ratings={ratings}
                                        workType={workType}
                                        taskTitle={taskTitle}
                                        taskId={detailsTaskId}
                                        tasksToCreate={tasksToCreate}
                                        tasksDependencies={tasksDependencies}
                                        netWorkTime={netWorkTime}
                                        grossWorkTime={grossWorkTime}
                                        rejectionReason={rejectionReason}
                                        solveByReviewer={solveByReviewer}
                                        seniorReviewerName={seniorReviewerName}
                                        expandedDescriptions={
                                            expandedDescriptions
                                        }
                                        blockerDescription={blockerDescription}
                                        attachments={attachments}
                                        reviewStatus={reviewStatus}
                                        onToggle={handleDescriptionToggle}
                                        priorityFrom={priority?.from}
                                        priorityTo={priority?.to}
                                    />
                                </ActivityColumn>
                            </ActivityRow>
                        );
                    })}
            </HistoryContainer>
        </StyledContainer>
    );
};

export default History;
